import {useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {TipTapEditor} from './TipTapEditor'

export interface IEmailSettingsValues {
  senderName?: string | null
  senderEmail?: string | null
  signatureHtml?: string | null
}

interface IResolvedSources {
  senderName?: string | null
  senderEmail?: string | null
  signatureHtml?: string | null
}

interface IResolvedEmailSettings extends IEmailSettingsValues {
  sources?: IResolvedSources
}

interface Props {
  values: IEmailSettingsValues | null | undefined
  resolved?: IResolvedEmailSettings | null
  showInheritance?: boolean
  saving?: boolean
  onSave: (input: IEmailSettingsValues) => void | Promise<void>
  // Si se pasa, se agrega un input + botón para enviar un email de prueba con
  // la configuración ya guardada (no la del formulario sin guardar).
  onSendTestEmail?: (toEmail: string) => void | Promise<void>
  sendingTestEmail?: boolean
}

const SOURCE_LABEL: Record<string, string> = {
  system: 'System',
  organization: 'Organization',
  user: 'User',
}

const FIELDS: {key: keyof IEmailSettingsValues; label: string}[] = [
  {key: 'senderName', label: 'Sender name'},
  {key: 'senderEmail', label: 'Sender email'},
]

export const EmailSettingsForm: React.FC<Props> = ({
  values,
  resolved,
  showInheritance = false,
  saving = false,
  onSave,
  onSendTestEmail,
  sendingTestEmail = false,
}) => {
  const [showPreview, setShowPreview] = useState(false)
  const [testEmailTo, setTestEmailTo] = useState('')
  const [form, setForm] = useState<IEmailSettingsValues>({
    senderName: values?.senderName || '',
    senderEmail: values?.senderEmail || '',
    signatureHtml: values?.signatureHtml || '',
  })
  const [overriding, setOverriding] = useState<Record<string, boolean>>({
    senderName: Boolean(values?.senderName),
    senderEmail: Boolean(values?.senderEmail),
    signatureHtml: Boolean(values?.signatureHtml),
  })

  useEffect(() => {
    setForm({
      senderName: values?.senderName || '',
      senderEmail: values?.senderEmail || '',
      signatureHtml: values?.signatureHtml || '',
    })
    setOverriding({
      senderName: Boolean(values?.senderName),
      senderEmail: Boolean(values?.senderEmail),
      signatureHtml: Boolean(values?.signatureHtml),
    })
  }, [values?.senderName, values?.senderEmail, values?.signatureHtml])

  const handleSave = () => {
    const input: IEmailSettingsValues = {}
    ;(['senderName', 'senderEmail', 'signatureHtml'] as const).forEach((key) => {
      if (showInheritance && !overriding[key]) {
        input[key] = null
      } else {
        input[key] = form[key] || null
      }
    })
    onSave(input)
  }

  const inheritedHint = (key: keyof IEmailSettingsValues) => {
    if (!showInheritance || !resolved) return null
    const source = resolved.sources?.[key]
    const value = resolved[key]
    if (!source || !value) return null
    return `Inherited from ${SOURCE_LABEL[source] || source}: ${key === 'signatureHtml' ? '(see signature)' : value}`
  }

  return (
    <div>
      {FIELDS.map(({key, label}) => (
        <Form.Group className='mb-4' key={key}>
          <Form.Label className='fw-bold d-flex align-items-center justify-content-between'>
            <span>{label}</span>
            {showInheritance && (
              <Form.Check
                type='switch'
                id={`override-${key}`}
                label='Override'
                checked={overriding[key]}
                onChange={(e) => setOverriding((p) => ({...p, [key]: e.target.checked}))}
              />
            )}
          </Form.Label>
          <Form.Control
            value={form[key] || ''}
            onChange={(e) => setForm((p) => ({...p, [key]: e.target.value}))}
            disabled={showInheritance && !overriding[key]}
            placeholder={showInheritance && !overriding[key] ? inheritedHint(key) || 'Inherits from the upper level' : ''}
          />
        </Form.Group>
      ))}

      <Form.Group className='mb-4'>
        <Form.Label className='fw-bold d-flex align-items-center justify-content-between'>
          <span>Signature (Email Signature)</span>
          <div className='d-flex align-items-center gap-3'>
            <Button variant='link' size='sm' className='p-0' onClick={() => setShowPreview(true)}>
              Preview
            </Button>
            {showInheritance && (
              <Form.Check
                type='switch'
                id='override-signatureHtml'
                label='Override'
                checked={overriding.signatureHtml}
                onChange={(e) => setOverriding((p) => ({...p, signatureHtml: e.target.checked}))}
              />
            )}
          </div>
        </Form.Label>
        {showInheritance && !overriding.signatureHtml ? (
          <div className='border rounded p-3 text-muted bg-light' style={{minHeight: 120}}>
            {resolved?.signatureHtml ? (
              <>
                <div className='fs-8 mb-2'>
                  {inheritedHint('signatureHtml')}
                </div>
                <div dangerouslySetInnerHTML={{__html: resolved.signatureHtml}} />
              </>
            ) : (
              <span className='fs-8'>No signature configured at any upper level.</span>
            )}
          </div>
        ) : (
          <TipTapEditor
            value={form.signatureHtml || ''}
            onChange={(html) => setForm((p) => ({...p, signatureHtml: html}))}
            height={220}
          />
        )}
      </Form.Group>

      <div className='d-flex align-items-center justify-content-between pt-2 border-top'>
        {onSendTestEmail ? (
          <div className='d-flex align-items-center gap-2'>
            <Form.Control
              type='email'
              size='sm'
              placeholder='Send test email to...'
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              style={{width: '220px'}}
            />
            <Button
              variant='light'
              size='sm'
              onClick={() => onSendTestEmail(testEmailTo)}
              disabled={sendingTestEmail || !testEmailTo}
            >
              {sendingTestEmail ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Sending...
                </>
              ) : (
                'Send test email'
              )}
            </Button>
          </div>
        ) : (
          <span />
        )}
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Signature preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {form.signatureHtml || (showInheritance && resolved?.signatureHtml) ? (
            <div
              dangerouslySetInnerHTML={{
                __html: (showInheritance && !overriding.signatureHtml ? resolved?.signatureHtml : form.signatureHtml) || '',
              }}
            />
          ) : (
            <span className='text-muted'>No signature to preview yet.</span>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}
